import {
  AlertDialog,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Separator,
  Skeleton,
  Spinner,
  Text,
} from "@radix-ui/themes";
import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CameraIcon,
  CheckCircledIcon,
  PlusIcon,
  MinusIcon,
} from "@radix-ui/react-icons";
import type { AvailableStore } from "../../../state/available-stores";
import { useStoreProofRequirements } from "../../../state/store-proof-requirements";
import {
  calculateStoreFlowState,
  ProofTarget,
  getProductRequirementLabel,
  type StoreFlowState,
  type ProductStepState,
} from "../../../lib/proofing-flow";
import { getGeolocation } from "../../../lib/geolocation";
import { supabase } from "../../../lib/supabase";
import { formatDistance } from "../../../lib/format-distance";

export interface StoreCardProps {
  store: AvailableStore;
  viewAsMode?: boolean;
  userId?: string;
}

function CheckIndicator({ checked }: { checked: boolean }) {
  if (checked) {
    return (
      <CheckCircledIcon
        width="24"
        height="24"
        style={{ color: "var(--jade-9)", flexShrink: 0 }}
      />
    );
  }
  return <Box style={{ width: 24, height: 24, flexShrink: 0 }} />;
}

function RequirementButton({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <Button
      size="2"
      variant="soft"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      disabled={disabled}
    >
      <CameraIcon />
      {label}
    </Button>
  );
}

function StoreRequirementSection({
  flowState,
  onCapture,
  t,
}: {
  flowState: StoreFlowState;
  onCapture: (target: typeof ProofTarget.STORE) => void;
  t: (key: string) => string;
}) {
  const { isRequired, isFulfilled } = flowState.storeStep;

  if (!isRequired) return null;

  return (
    <Box>
      <Flex align="center" gap="2" mb="2">
        <CheckIndicator checked={isFulfilled} />
        <Text size="2" weight="bold">
          {t("proofing.storeFrontPicture")}
        </Text>
      </Flex>
      {!isFulfilled && (
        <Flex direction="column" gap="2" style={{ marginLeft: 32 }} pr="5">
          <RequirementButton
            label={t("proofing.takeStoreFrontPicture")}
            onClick={() => onCapture(ProofTarget.STORE)}
            disabled={false}
          />
          <Text size="1" color="gray">
            {t("proofing.storeFrontRequired")}
          </Text>
        </Flex>
      )}
    </Box>
  );
}

function ProductRequirementSection({
  productStep,
  onCapture,
  locale,
}: {
  productStep: ProductStepState;
  onCapture: (
    target: typeof ProofTarget.PRODUCT,
    storeActivityId: number,
    productName: string,
    productRequirementId: number,
    productRequirementLabel: string,
    productRequirementCode: string,
  ) => void;
  locale: string;
}) {
  const isComplete = productStep.isComplete;

  return (
    <Box>
      <Flex align="center" gap="2" mb="2">
        <CheckIndicator checked={isComplete} />
        <Text size="2" weight="bold">
          {productStep.productName}
        </Text>
      </Flex>
      {!isComplete && (
        <Flex direction="column" gap="2" style={{ marginLeft: 32 }} pr="5">
          {productStep.requirements.map((req, index) => {
            const label = getProductRequirementLabel(
              req.productRequirement,
              locale,
            );
            const seq = index + 1;
            if (req.isFulfilled) {
              return (
                <Flex
                  key={req.productRequirement.id}
                  align="center"
                  gap="2"
                  py="1"
                >
                  <Text size="2">#{seq}</Text>
                  <Badge color="jade" variant="soft" size="1">
                    ✓
                  </Badge>
                </Flex>
              );
            }
            return (
              <RequirementButton
                key={req.productRequirement.id}
                label={`#${seq}`}
                onClick={() =>
                  onCapture(
                    ProofTarget.PRODUCT,
                    productStep.storeActivityId,
                    productStep.productName,
                    req.productRequirement.id,
                    label,
                    req.productRequirement.code,
                  )
                }
                disabled={req.isBlocked}
              />
            );
          })}
        </Flex>
      )}
    </Box>
  );
}

function ExpandedContent({
  flowState,
  onStoreCapture,
  onProductCapture,
  locale,
  t,
}: {
  flowState: StoreFlowState;
  onStoreCapture: (target: typeof ProofTarget.STORE) => void;
  onProductCapture: (
    target: typeof ProofTarget.PRODUCT,
    storeActivityId: number,
    productName: string,
    productRequirementId: number,
    productRequirementLabel: string,
    productRequirementCode: string,
  ) => void;
  locale: string;
  t: (key: string) => string;
}) {
  return (
    <Box pt="3">
      {flowState.storeStep.isRequired && (
        <StoreRequirementSection
          flowState={flowState}
          onCapture={onStoreCapture}
          t={t}
        />
      )}

      {flowState.productSteps.map((productStep, index) => (
        <Box key={productStep.storeActivityId}>
          {(index > 0 || flowState.storeStep.isRequired) && (
            <Separator size="4" my="3" />
          )}
          <ProductRequirementSection
            productStep={productStep}
            onCapture={onProductCapture}
            locale={locale}
          />
        </Box>
      ))}
    </Box>
  );
}

export function StoreCard({ store, viewAsMode, userId }: StoreCardProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const { requirements, loading } = useStoreProofRequirements(
    store.store_id,
    userId,
  );

  const locale = i18n.language?.split("-")[0] || "en";

  const distance =
    store.distance_meters != null
      ? formatDistance(store.distance_meters)
      : t("proofing.distanceUnknown");

  const flowState = useMemo(() => {
    if (!requirements) return null;
    return calculateStoreFlowState(requirements, store.distance_meters);
  }, [requirements, store.distance_meters]);

  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [distanceWarning, setDistanceWarning] = useState<{
    distanceMeters: number;
    routeState: Record<string, unknown>;
  } | null>(null);

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  /**
   * Fetch fresh GPS, compute distance, then either navigate directly
   * or show a warning if >100m from the store.
   */
  const navigateWithFreshPosition = useCallback(
    async (routeState: Record<string, unknown>) => {
      setIsFetchingLocation(true);
      try {
        const pos = await getGeolocation();
        const stateWithPosition = {
          ...routeState,
          latitude: pos.latitude,
          longitude: pos.longitude,
          accuracy: pos.accuracy,
        };

        // Compute distance to store
        const { data: distanceMeters } = await supabase.rpc(
          "get_distance_to_store",
          {
            p_store_id: store.store_id,
            p_lat: pos.latitude,
            p_lng: pos.longitude,
          },
        );

        if (distanceMeters != null && distanceMeters > 100) {
          setDistanceWarning({
            distanceMeters,
            routeState: stateWithPosition,
          });
        } else {
          navigate(`/proofing/store/${store.store_id}/capture`, {
            state: stateWithPosition,
          });
        }
      } catch (error) {
        console.error("Error fetching location:", error);
        // Fallback: navigate without fresh position
        navigate(`/proofing/store/${store.store_id}/capture`, {
          state: routeState,
        });
      } finally {
        setIsFetchingLocation(false);
      }
    },
    [navigate, store.store_id],
  );

  const handleStoreCapture = () => {
    if (viewAsMode) return;
    navigateWithFreshPosition({
      target: ProofTarget.STORE,
      store_id: store.store_id,
      store_name: store.store_name,
    });
  };

  const handleProductCapture = (
    _target: typeof ProofTarget.PRODUCT,
    storeActivityId: number,
    productName: string,
    productRequirementId: number,
    productRequirementLabel: string,
    productRequirementCode: string,
  ) => {
    if (viewAsMode) return;
    navigateWithFreshPosition({
      target: ProofTarget.PRODUCT,
      store_id: store.store_id,
      store_name: store.store_name,
      store_activity_id: storeActivityId,
      product_requirement_id: productRequirementId,
      product_requirement_label: productRequirementLabel,
      product_requirement_code: productRequirementCode,
      product_name: productName,
    });
  };

  const isComplete = flowState?.isComplete ?? false;
  const picturesNeeded = flowState?.totalPicturesNeeded ?? 0;

  return (
    <Card
      style={{
        backgroundColor: "var(--gray-2)",
        width: "100%",
      }}
    >
      {/* Header - always visible */}
      <Flex
        gap="3"
        align="center"
        justify="between"
        onClick={handleToggle}
        style={{ cursor: "pointer" }}
      >
        <Flex direction="column" gap="1" style={{ flex: 1 }}>
          <Text as="div" size="2" weight="bold">
            {store.store_name}
          </Text>
        </Flex>

        <Flex align="center" gap="2">
          <Text size="2" color="gray">
            📍 {distance}
          </Text>

          {loading ? (
            <Skeleton width="40px" height="24px" />
          ) : isComplete ? (
            <CheckCircledIcon
              width="24"
              height="24"
              style={{ color: "var(--jade-9)" }}
            />
          ) : (
            <Badge color="amber" size="2">
              <CameraIcon /> {picturesNeeded}
            </Badge>
          )}

          <Button
            size="1"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
            }}
          >
            {expanded ? <MinusIcon /> : <PlusIcon />}
          </Button>
        </Flex>
      </Flex>

      {/* Expanded content */}
      {expanded && flowState && (
        <ExpandedContent
          flowState={flowState}
          onStoreCapture={handleStoreCapture}
          onProductCapture={handleProductCapture}
          locale={locale}
          t={t}
        />
      )}

      {expanded && loading && (
        <Box pt="3">
          <Skeleton height="60px" />
          <Separator size="4" my="3" />
          <Skeleton height="80px" />
        </Box>
      )}

      {/* Loading overlay while fetching location */}
      {isFetchingLocation && (
        <Flex align="center" justify="center" gap="2" pt="3">
          <Spinner size="1" />
          <Text size="2" color="gray">
            {t("proofing.fetchingLocation")}
          </Text>
        </Flex>
      )}

      {/* Distance warning dialog */}
      <AlertDialog.Root
        open={!!distanceWarning}
        onOpenChange={(open) => !open && setDistanceWarning(null)}
      >
        <AlertDialog.Content maxWidth="450px">
          <AlertDialog.Title>
            {t("proofing.distanceWarning.title")}
          </AlertDialog.Title>
          <AlertDialog.Description size="2">
            {t("proofing.distanceWarning.message", {
              distance: distanceWarning
                ? formatDistance(distanceWarning.distanceMeters)
                : "",
            })}
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">
                {t("common.cancel")}
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                color="amber"
                onClick={() => {
                  if (distanceWarning) {
                    navigate(`/proofing/store/${store.store_id}/capture`, {
                      state: distanceWarning.routeState,
                    });
                    setDistanceWarning(null);
                  }
                }}
              >
                {t("proofing.distanceWarning.continue")}
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </Card>
  );
}
